// biome-ignore-all lint/a11y: TODO - address a11y issues later
import { InfoCircledIcon } from "@radix-ui/react-icons";
import { View } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { StandaloneFilterSelect } from "@/components/ui/filter-select";
import { InfoTooltip } from "@/components/ui/info-tooltip";
import { Switch } from "@/components/ui/switch";
import { useCampaignStatuses } from "@/hooks/api/use-campaign-metrics";
import { useCampaignStatusSelection } from "@/hooks/use-campaign-status-selection";

interface CampaignStatusSelectorProps {
  campaignId?: string;
}

export function CampaignStatusSelector({
  campaignId,
}: CampaignStatusSelectorProps) {
  const { data: statusesData, isLoading } = useCampaignStatuses(campaignId);
  const {
    selectedStatuses,
    handleStatusesChange,
    auto,
    setAuto,
    hasOverride,
    clearAllOverrides,
  } = useCampaignStatusSelection(campaignId);

  // Function to get default engagement statuses
  const getDefaultEngagementStatuses = useCallback((): string[] => {
    if (
      campaignId &&
      statusesData?.statuses &&
      statusesData.statuses.length > 0
    ) {
      // Get statuses that represent engagement
      const engagementStatuses = statusesData.statuses
        .filter((statusObj) => statusObj.represents_engagement)
        .map((statusObj) => statusObj.status);

      return engagementStatuses;
    }
    return [];
  }, [campaignId, statusesData]);

  // Compute default engagement statuses without writing to storage
  const defaultEngagementStatuses = useMemo(
    () => getDefaultEngagementStatuses(),
    [getDefaultEngagementStatuses],
  );

  // Automatic mode is page-level
  const [automatic, setAutomatic] = useState(!!auto);
  useEffect(() => {
    setAutomatic(!!auto);
  }, [auto]);

  // When stored auto is OFF and a new campaign loads without an override, seed
  // the per-campaign filters with the server defaults so the selection
  // is persisted even if the user doesn't interact.
  useEffect(() => {
    if (
      !auto &&
      campaignId &&
      !hasOverride &&
      defaultEngagementStatuses.length > 0
    ) {
      handleStatusesChange([...defaultEngagementStatuses]);
    }
  }, [
    auto,
    campaignId,
    hasOverride,
    defaultEngagementStatuses,
    handleStatusesChange,
  ]);

  // Format options for filter
  const statusOptions =
    statusesData?.statuses?.map((statusObj) => ({
      id: statusObj.status,
      label: statusObj.status,
    })) || [];

  // Show loading state if necessary
  if (isLoading || !campaignId) {
    return (
      <div className="h-8 w-[200px] bg-muted animate-pulse rounded-lg corner-smooth-lg"></div>
    );
  }

  return (
    <StandaloneFilterSelect
      options={statusOptions}
      // When Automatic is on, always show server defaults; otherwise use user override (if any)
      selectedValues={
        automatic
          ? [...defaultEngagementStatuses]
          : hasOverride
            ? selectedStatuses
            : defaultEngagementStatuses
      }
      triggerPrefix="Statuses"
      triggerPrefixIcon={<View className="h-4 w-4" />}
      emptyTriggerText="none selected"
      valueTextOverride={automatic ? "Auto" : undefined}
      popoverHeader={
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium">Automatic</span>
            <InfoTooltip
              trigger={
                <button className="cursor-pointer">
                  <InfoCircledIcon className="h-4 w-4 text-muted-foreground" />
                </button>
              }
              side="bottom"
              delayDuration={300}
              suppressInitialHoverMs={500}
              className="w-[280px]"
            >
              <div className="text-sm space-y-2">
                <p>
                  Uses recommended engagement statuses (those marked
                  <span className="font-medium"> Response</span> in Salesforce).
                </p>
                <p>
                  Turn off to customize which statuses qualify as engagement.
                </p>
              </div>
            </InfoTooltip>
          </div>
          <Switch checked={automatic} onCheckedChange={setAutomatic} />
        </div>
      }
      contentDisabled={automatic}
      onApply={(values?: string[]) => {
        if (automatic) {
          // Clear override to use server defaults
          clearAllOverrides();
          setAuto(true);
          // Keep local toggle in sync with stored flag
          setAutomatic(true);
          return true; // handled
        }
        setAuto(false);
        setAutomatic(false);
        // If user applied with no selections, treat as intentional empty override
        if (!values || values.length === 0) {
          handleStatusesChange([], { intentionalClear: true });
          return true; // handled here
        }
        // default apply path (component will call onFilterChange)
        return false;
      }}
      onCancel={() => {
        // Re-sync the automatic toggle from stored value on cancel/dismiss
        setAutomatic(!!auto);
      }}
      onSelectionChange={(vals) =>
        handleStatusesChange(vals, {
          intentionalClear: vals.length === 0,
        })
      }
      label="Qualifying statuses"
      emptyMessage="No statuses available"
      searchPlaceholder="Search statuses..."
      width={"auto"}
      applyButtonText="Apply"
      // Reset option removed per spec
      triggerClassName="justify-between h-8 px-3 text-xs"
      popover={{
        title: "Qualifying Engagement Statuses",
        content: (
          <p>
            Select statuses that represent engagement with your campaign. This
            will filter the metrics displayed in the campaign report card.
          </p>
        ),
        width: "280px",
      }}
    />
  );
}
